<template>
  <v-form
    ref="form"
    lazy-validation
    v-model="formValid"
    v-if="isLoaded()"
    @submit.prevent="save()"
    class="LaunchTaskForm"
  >
    <v-alert
      :value="formError"
      color="error"
      class="LaunchTaskForm__alert"
    >{{ formError }}
    </v-alert>

    <v-alert
      dismissible
      dense
      @input="item.commit_hash=null"
      v-model="hasCommit"
      class="LaunchTaskForm__commit"
    >
      <div class="LaunchTaskForm__commit-head">
        <ZpIcon name="git-fork" :size="14" />
        {{ (item.commit_hash || '').substr(0, 10) }}
      </div>
      <div v-if="sourceTask && sourceTask.commit_message">
        {{ sourceTask.commit_message.substring(0, 50) }}
      </div>
    </v-alert>

    <div class="LaunchTaskForm__layout">
      <aside class="LaunchTaskForm__readiness">
        <span class="zp-overline">{{ $t('launchReadiness') }}</span>
        <div
          v-for="signal in readinessItems"
          :key="signal.key"
          class="LaunchTaskForm__signal"
        >
          <span class="LaunchTaskForm__signal-icon" :class="`is-${signal.variant}`">
            <ZpIcon :name="signal.icon" :size="14" />
          </span>
          <span>
            <strong>{{ signal.label }}</strong>
            <small>{{ signal.value }}</small>
          </span>
        </div>
      </aside>

      <section class="LaunchTaskForm__workspace">
        <div class="LaunchTaskForm__tabs" role="tablist">
          <button
            v-for="tab in launchTabs"
            :key="tab.key"
            class="LaunchTaskForm__tab"
            :class="{ 'is-active': activeTab === tab.key }"
            type="button"
            @click="activeTab = tab.key"
          >
            <ZpIcon :name="tab.icon" :size="15" />
            {{ tab.label }}
          </button>
        </div>

        <div class="LaunchTaskForm__sections">
          <section v-show="activeTab === 'setup'" class="LaunchTaskForm__section">
            <v-autocomplete
              v-if="buildTasks != null && template.type === 'deploy'"
              v-model="item.build_task_id"
              :label="$t('buildVersion')"
              :items="buildTasks"
              item-value="id"
              :item-text="(itm) => getTaskMessage(itm)"
              :rules="[v => !!v || $t('build_version_required')]"
              required
              :disabled="formSaving"
              outlined
              dense
            />

            <v-skeleton-loader
              v-else-if="template.type === 'deploy'"
              type="card"
              height="54"
              class="LaunchTaskForm__loader"
            ></v-skeleton-loader>

            <v-textarea
              v-model="item.message"
              :label="$t('messageOptional')"
              :disabled="formSaving"
              outlined
              dense
              auto-grow
              rows="3"
            />

            <v-text-field
              v-model="git_branch"
              :label="fieldLabel('branch')"
              outlined
              dense
              required
              :disabled="formSaving"
              v-if="
                needField('allow_override_branch')
                && template.allow_override_branch_in_task"
            />

            <v-autocomplete
              v-model="inventory_id"
              :label="fieldLabel('inventory')"
              :items="inventory"
              item-value="id"
              item-text="name"
              outlined
              dense
              required
              :disabled="formSaving"
              v-if="inventory != null && needInventory"
            ></v-autocomplete>

            <v-skeleton-loader
              v-else-if="needInventory"
              type="card"
              height="46"
              class="LaunchTaskForm__loader"
            ></v-skeleton-loader>
          </section>

          <section v-show="activeTab === 'prompts'" class="LaunchTaskForm__section">
            <div v-if="surveyVars.length > 0">
              <div v-for="(v) in surveyVars" :key="v.name">
                <v-text-field
                  v-if="v.type === 'secret'"
                  :label="v.title"
                  :hint="v.description"
                  v-model="editedSecretEnvironment[v.name]"
                  :required="v.required"
                  class="masked-secret-input"
                  :rules="[
                    val => !v.required || !!val || v.title + $t('isRequired'),
                  ]"
                  outlined
                  dense
                />

                <v-select
                  clearable
                  v-else-if="v.type === 'enum'"
                  :label="v.title + (v.required ? ' *' : '')"
                  :hint="v.description"
                  v-model="editedEnvironment[v.name]"
                  :required="v.required"
                  :rules="[
                    val => !v.required || val != null || v.title + ' ' + $t('isRequired')
                  ]"
                  :items="v.values"
                  item-text="name"
                  item-value="value"
                  outlined
                  dense
                />

                <v-text-field
                  v-else
                  :label="v.title + (v.required ? ' *' : '')"
                  :hint="v.description"
                  v-model="editedEnvironment[v.name]"
                  :required="v.required"
                  :rules="[
                    val => !v.required || !!val || v.title + ' ' + $t('isRequired'),
                    val => !val || v.type !== 'int' || /^\\d+$/.test(val) ||
                    v.title + ' ' + $t('mustBeInteger'),
                  ]"
                  outlined
                  dense
                />
              </div>
            </div>
            <div v-else class="LaunchTaskForm__empty">
              <ZpIcon name="check-circle" :size="20" />
              <strong>{{ $t('launchNoPrompts') }}</strong>
              <span>{{ $t('launchNoPromptsHint') }}</span>
            </div>
          </section>

          <section v-show="activeTab === 'runtime'" class="LaunchTaskForm__section">
            <TaskParamsAnsibleForm
              v-if="template.app === 'ansible'"
              v-model="item.params"
              :app="template.app"
              :template-params="template.task_params || {}"
            />

            <TaskParamsTerraformForm
              v-else-if="['terraform', 'tofu', 'terragrunt'].includes(template.app)"
              v-model="item.params"
              :app="template.app"
              :template-params="template.task_params || {}"
            />

            <div v-else class="LaunchTaskForm__empty">
              <ZpIcon name="sliders-horizontal" :size="20" />
              <strong>{{ $t('launchRuntimeLocked') }}</strong>
              <span>{{ $t('launchRuntimeLockedHint') }}</span>
            </div>
          </section>

          <section v-show="activeTab === 'arguments'" class="LaunchTaskForm__section">
            <ArgsPicker
              v-if="template.allow_override_args_in_task"
              :vars="args"
              :title="$t('templateComposerCliArgs')"
              @change="setArgs"
            />

            <div v-else class="LaunchTaskForm__empty">
              <ZpIcon name="square-terminal" :size="20" />
              <strong>{{ $t('launchArgumentsLocked') }}</strong>
              <span>{{ $t('launchArgumentsLockedHint') }}</span>
            </div>
          </section>

          <section v-show="activeTab === 'review'" class="LaunchTaskForm__section">
            <div class="LaunchTaskForm__review">
              <div v-for="row in reviewRows" :key="row.label">
                <span>{{ row.label }}</span>
                <strong>{{ row.value }}</strong>
              </div>
            </div>
          </section>
        </div>
      </section>

      <aside class="LaunchTaskForm__plan">
        <span class="zp-overline">{{ $t('launchRunPlan') }}</span>
        <div class="LaunchTaskForm__plan-group">
          <strong>{{ $t('launchExecutionTarget') }}</strong>
          <div>
            <span>{{ $t('application') }}</span>
            <b>{{ appTitle }}</b>
          </div>
          <div>
            <span>{{ fieldLabel('branch') }}</span>
            <b class="zp-tabular">{{ branchSummary }}</b>
          </div>
        </div>
        <div class="LaunchTaskForm__plan-group">
          <strong>{{ $t('launchOperatorInputs') }}</strong>
          <div>
            <span>{{ $t('launchRequiredPrompts') }}</span>
            <b class="zp-tabular">{{ requiredSurveyCount }}</b>
          </div>
          <div>
            <span>{{ $t('launchOptionalPrompts') }}</span>
            <b class="zp-tabular">{{ optionalSurveyCount }}</b>
          </div>
          <div>
            <span>{{ $t('templateComposerCliArgs') }}</span>
            <b class="zp-tabular">{{ args.length }}</b>
          </div>
        </div>
      </aside>
    </div>
  </v-form>
</template>
<style lang="scss">
.LaunchTaskForm {
  color: var(--zp-text-primary);

  &__alert {
    margin: var(--zp-space-md);
  }

  &__commit {
    margin: var(--zp-space-md);
    border: var(--zp-border-hair) solid var(--zp-brand);
    background: var(--zp-brand-soft) !important;
    color: var(--zp-text-primary) !important;
  }

  &__commit-head {
    display: inline-flex;
    align-items: center;
    gap: var(--zp-space-2xs);
    font-weight: 700;
  }

  &__layout {
    display: grid;
    grid-template-columns: 220px minmax(0, 1fr) 284px;
    min-height: 560px;
  }

  &__readiness,
  &__plan {
    padding: var(--zp-space-lg);
    background: var(--zp-bg-page);
  }

  &__readiness {
    border-right: var(--zp-border-hair) solid var(--zp-border-hairline);
  }

  &__plan {
    border-left: var(--zp-border-hair) solid var(--zp-border-hairline);
  }

  &__signal {
    display: grid;
    grid-template-columns: 28px minmax(0, 1fr);
    gap: var(--zp-space-xs);
    margin-top: var(--zp-space-lg);

    strong,
    small {
      display: block;
      line-height: var(--zp-leading-snug);
    }

    strong {
      color: var(--zp-text-primary);
      font-size: var(--zp-text-sm);
      font-weight: 700;
    }

    small {
      margin-top: 2px;
      color: var(--zp-text-tertiary);
      font-size: var(--zp-text-xs);
    }
  }

  &__signal-icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    border-radius: var(--zp-radius-md);
    background: var(--zp-status-neutral-bg);
    color: var(--zp-status-neutral);

    &.is-success {
      background: var(--zp-status-success-bg);
      color: var(--zp-status-success);
    }

    &.is-warning {
      background: var(--zp-status-warning-bg);
      color: var(--zp-status-warning);
    }
  }

  &__workspace {
    min-width: 0;
  }

  &__tabs {
    display: grid;
    grid-template-columns: repeat(5, minmax(0, 1fr));
    border-bottom: var(--zp-border-hair) solid var(--zp-border-hairline);
    background: var(--zp-bg-surface);
  }

  &__tab {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: var(--zp-space-2xs);
    height: 48px;
    border: 0;
    border-bottom: 2px solid transparent;
    background: transparent;
    color: var(--zp-text-secondary);
    font-size: var(--zp-text-sm);
    font-weight: 700;
    cursor: pointer;

    &.is-active {
      border-bottom-color: var(--zp-brand);
      color: var(--zp-brand);
    }
  }

  &__sections {
    padding: var(--zp-space-lg);
  }

  &__section {
    display: grid;
    gap: var(--zp-space-sm);
  }

  &__loader {
    margin-bottom: var(--zp-space-sm);
  }

  &__empty {
    display: grid;
    place-items: center;
    gap: var(--zp-space-xs);
    min-height: 220px;
    padding: var(--zp-space-xl);
    border: var(--zp-border-hair) dashed var(--zp-border-default);
    border-radius: var(--zp-radius-md);
    color: var(--zp-text-tertiary);
    text-align: center;

    strong {
      color: var(--zp-text-primary);
      font-size: var(--zp-text-sm);
    }

    span {
      max-width: 36ch;
      font-size: var(--zp-text-xs);
      line-height: var(--zp-leading-normal);
    }
  }

  &__plan-group {
    margin-top: var(--zp-space-lg);
    padding-top: var(--zp-space-md);
    border-top: var(--zp-border-hair) solid var(--zp-border-hairline);

    strong {
      display: block;
      margin-bottom: var(--zp-space-sm);
      color: var(--zp-text-secondary);
      font-size: var(--zp-text-xs);
      font-weight: 700;
      letter-spacing: var(--zp-tracking-overline);
      text-transform: uppercase;
    }

    div {
      display: flex;
      justify-content: space-between;
      gap: var(--zp-space-sm);
      margin-top: var(--zp-space-xs);
      color: var(--zp-text-secondary);
      font-size: var(--zp-text-xs);
    }

    b {
      color: var(--zp-text-primary);
      font-weight: 650;
      text-align: right;
    }
  }

  &__review {
    border: var(--zp-border-hair) solid var(--zp-border-hairline);
    border-radius: var(--zp-radius-md);
    overflow: hidden;

    div {
      display: flex;
      justify-content: space-between;
      gap: var(--zp-space-md);
      padding: var(--zp-space-sm) var(--zp-space-md);
      border-top: var(--zp-border-hair) solid var(--zp-border-hairline);
      font-size: var(--zp-text-sm);

      &:first-child {
        border-top: 0;
      }
    }

    span {
      color: var(--zp-text-secondary);
    }

    strong {
      color: var(--zp-text-primary);
      text-align: right;
    }
  }
}

@media (max-width: 960px) {
  .LaunchTaskForm {
    &__layout {
      grid-template-columns: 1fr;
      min-height: auto;
    }

    &__readiness,
    &__plan {
      border: 0;
      border-bottom: var(--zp-border-hair) solid var(--zp-border-hairline);
    }

    &__readiness {
      display: none;
    }
  }
}

@media (max-width: 700px) {
  .LaunchTaskForm {
    &__tabs {
      display: flex;
      overflow-x: auto;
    }

    &__tab {
      flex: 0 0 auto;
      padding: 0 var(--zp-space-sm);
    }

    &__sections,
    &__plan {
      padding: var(--zp-space-md);
    }
  }
}
</style>
<script>
/* eslint-disable import/no-extraneous-dependencies,import/extensions */

import ItemFormBase from '@/components/ItemFormBase';
import axios from 'axios';
import ArgsPicker from '@/components/ArgsPicker.vue';
import AppFieldsMixin from '@/components/AppFieldsMixin';
import TaskParamsAnsibleForm from '@/components/TaskParamsAnsibleForm.vue';
import TaskParamsTerraformForm from '@/components/TaskParamsTerraformForm.vue';
import { APP_SHORT_TITLE } from '@/lib/constants';

export default {
  mixins: [ItemFormBase, AppFieldsMixin],

  props: {
    template: Object,
    sourceTask: Object,
  },

  components: {
    TaskParamsAnsibleForm,
    TaskParamsTerraformForm,
    ArgsPicker,
  },

  data() {
    return {
      buildTasks: null,
      activeTab: 'setup',
      hasCommit: null,
      editedEnvironment: null,
      editedSecretEnvironment: null,
      cmOptions: {
        tabSize: 2,
        mode: 'application/json',
        lineNumbers: true,
        line: true,
        lint: true,
        indentWithTabs: false,
      },
      inventory: null,
    };
  },

  computed: {
    launchTabs() {
      return [
        { key: 'setup', label: this.$t('launchTabSetup'), icon: 'settings' },
        { key: 'prompts', label: this.$t('launchTabPrompts'), icon: 'circle-help' },
        { key: 'runtime', label: this.$t('launchTabRuntime'), icon: 'play' },
        { key: 'arguments', label: this.$t('launchTabArguments'), icon: 'code' },
        { key: 'review', label: this.$t('launchTabReview'), icon: 'clipboard-check' },
      ];
    },

    surveyVars() {
      return this.template.survey_vars || [];
    },

    requiredSurveyCount() {
      return this.surveyVars.filter((v) => v.required).length;
    },

    optionalSurveyCount() {
      return this.surveyVars.length - this.requiredSurveyCount;
    },

    needInventory() {
      return this.needField('inventory') && this.template.task_params?.allow_override_inventory;
    },

    args() {
      let res = this.item.arguments;

      if (res == null) {
        res = this.template.arguments;
      }

      if (res == null) {
        res = '[]';
      }

      return JSON.parse(res);
    },

    app() {
      return this.template.app;
    },

    appTitle() {
      return APP_SHORT_TITLE[this.app] || this.app || 'Ansible';
    },

    branchSummary() {
      return this.git_branch || this.$t('launchTemplateDefault');
    },

    inventorySummary() {
      if (!this.needInventory) return this.$t('launchTemplateDefault');
      const selected = (this.inventory || []).find((item) => item.id === this.inventory_id);
      return selected?.name || this.$t('launchNotSelected');
    },

    buildSummary() {
      if (this.template.type !== 'deploy') return this.$t('launchNotRequired');
      const selected = (this.buildTasks || []).find((task) => task.id === this.item.build_task_id);
      return selected ? this.getTaskMessage(selected) : this.$t('launchNotSelected');
    },

    readinessItems() {
      const promptVariant = this.requiredSurveyCount > 0 ? 'warning' : 'success';
      return [
        {
          key: 'template',
          label: this.$t('template'),
          value: this.template.name || this.$t('newTask'),
          variant: 'success',
          icon: 'check',
        },
        {
          key: 'inventory',
          label: this.fieldLabel('inventory'),
          value: this.inventorySummary,
          variant: this.needInventory && !this.inventory_id ? 'warning' : 'success',
          icon: this.needInventory && !this.inventory_id ? 'triangle-alert' : 'check',
        },
        {
          key: 'prompts',
          label: this.$t('launchTabPrompts'),
          value: this.$t('launchPromptCount', { count: this.surveyVars.length }),
          variant: promptVariant,
          icon: this.requiredSurveyCount > 0 ? 'triangle-alert' : 'check',
        },
        {
          key: 'runtime',
          label: this.$t('launchTabRuntime'),
          value: this.appTitle,
          variant: 'success',
          icon: 'check',
        },
      ];
    },

    reviewRows() {
      return [
        { label: this.$t('template'), value: this.template.name || '—' },
        { label: this.$t('application'), value: this.appTitle },
        { label: this.$t('buildVersion'), value: this.buildSummary },
        { label: this.fieldLabel('branch'), value: this.branchSummary },
        { label: this.fieldLabel('inventory'), value: this.inventorySummary },
        { label: this.$t('launchRequiredPrompts'), value: this.requiredSurveyCount },
      ];
    },

    inventory_id: {
      get() {
        return (this.item || {}).inventory_id || this.template.inventory_id;
      },
      set(newValue) {
        this.item.inventory_id = newValue;
      },
    },

    git_branch: {
      get() {
        return (this.item || {}).git_branch || this.template.git_branch;
      },
      set(newValue) {
        this.item.git_branch = newValue;
      },
    },
  },

  watch: {
    needReset(val) {
      if (val) {
        if (this.item) {
          this.item.template_id = this.template.id;
        }
        this.buildTasks = null;
        this.inventory = null;
        // this.template = null;
      }
    },

    template(val) {
      if (this.item) {
        this.item.template_id = val?.id;
      }
    },

    sourceTask(val) {
      this.assignItem(val);
    },

    hasCommit(val) {
      if (val == null) {
        this.commit_hash = null;
      }
    },
  },

  created() {
    this.refreshItem();
  },

  methods: {

    setArgs(args) {
      this.item.arguments = JSON.stringify(args || []);
    },

    getTaskMessage(task) {
      let buildTask = task;

      while (buildTask.version == null && buildTask.build_task != null) {
        buildTask = buildTask.build_task;
      }

      if (!buildTask) {
        return '';
      }

      return buildTask.version + (buildTask.message ? ` — ${buildTask.message}` : '');
    },

    assignItem(val) {
      const v = val || {};

      if (this.item == null) {
        this.item = {};
      }

      Object.keys(v).forEach((field) => {
        this.item[field] = v[field];
      });

      this.editedEnvironment = JSON.parse(v.environment || '{}');
      this.editedSecretEnvironment = JSON.parse(v.secret || '{}');
      this.hasCommit = v.commit_hash != null;
    },

    isLoaded() {
      return this.item != null && this.template != null;
    },

    beforeSave() {
      this.item.environment = JSON.stringify(this.editedEnvironment);
      this.item.secret = JSON.stringify(this.editedSecretEnvironment);
    },

    refreshItem() {
      this.assignItem(this.sourceTask);

      this.item.template_id = this.template.id;

      if (!this.item.params) {
        this.item.params = {};
      }

      ['tags', 'limit', 'skip_tags'].forEach((param) => {
        if (!this.item.params[param]) {
          this.item.params[param] = (this.template.task_params || {})[param];
        }
      });
    },

    async afterLoadData() {
      this.refreshItem();

      [
        this.buildTasks,
        this.inventory,
      ] = await Promise.all([

        this.template.type === 'deploy' ? (await axios({
          keys: 'get',
          url: `/api/project/${this.projectId}/templates/${this.template.build_template_id}/tasks?status=success&limit=20`,
          responseType: 'json',
        })).data.filter((task) => task.status === 'success') : [],

        this.needInventory ? (await axios({
          keys: 'get',
          url: this.getInventoryUrl(),
          responseType: 'json',
        })).data : [],
      ]);

      if (this.item.build_task_id == null
        && this.buildTasks.length > 0
        && this.buildTasks.length > 0) {
        this.item.build_task_id = this.buildTasks[0].id;
      }

      ['tags', 'limit', 'skip_tags'].forEach((param) => {
        if (!this.item.params[param]) {
          this.item.params[param] = (this.template.task_params || {})[param];
        }
      });

      const defaultVars = (this.template.survey_vars || [])
        .filter((s) => s.default_value)
        .reduce((res, curr) => ({
          ...res,
          [curr.name]: curr.default_value,
        }), {});

      this.editedEnvironment = {
        ...defaultVars,
        ...this.editedEnvironment,
      };
    },

    getInventoryUrl() {
      let res = `/api/project/${this.projectId}/inventory?app=${this.app}`;
      switch (this.app) {
        case 'terraform':
        case 'tofu':
          res += `&template_id=${this.template.id}`;
          break;
        default:
          break;
      }
      return res;
    },

    getItemsUrl() {
      return `/api/project/${this.projectId}/tasks`;
    },
  },
};
</script>
